import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { colors } from "@/styles/constants";
import { Account } from "@/types/Account";
import { AccountCollapsible } from "./AccountCollapsible";
import { Header } from "@/components/atoms/Header";
import { isAdditionalServices } from "@/utils/accounts";
import { DashboardInfoContext } from "@/store/DashboardProvider";
import { Stack, Typography } from "@mui/material";

type Sections = {
  additionalServicesAccount: Array<Account>;
};

export const AdditionalServicesAccount: React.FC = () => {
  const {
    state: { signature, caseId, accounts, isExpressConvertedToNEForm },
  } = React.useContext(DashboardInfoContext);

  const sectionsOfServices = React.useMemo(() => {
    const sections = { additionalServicesAccount: [] } as Sections;
    if (!accounts) return sections;

    accounts.forEach((account) => {
      if (isAdditionalServices(account)) {
        sections.additionalServicesAccount.push(account);
      }
    });
    return sections;
  }, [accounts]);

  const [highlightButton, setHighlightButton] = React.useState(false);
  const [searchParams] = useSearchParams();
  const account_id = searchParams.get("account_id");
  const add_accounts_focus = searchParams.get("add_accounts") === "true";

  React.useEffect(() => {
    if (account_id) {
      const element = document.getElementById(account_id);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
  }, [account_id]);

  React.useEffect(() => {
    if (add_accounts_focus) {
      setHighlightButton(true);

      const timeoutId = setTimeout(() => setHighlightButton(false), 2000);
      const buttonElement = document.getElementById("add-account-button");

      if (buttonElement) {
        buttonElement.scrollIntoView({ behavior: "smooth", block: "center" });
        buttonElement.focus({ preventScroll: true });
      }

      return () => clearTimeout(timeoutId);
    }
  }, [add_accounts_focus]);

  return (
    <Stack spacing={3} sx={{ paddingTop: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <h2> Additional Services</h2>
      </Stack>

      <Section title="">
        {sectionsOfServices.additionalServicesAccount.length === 0 ? (
          <NoAccountsText>
            No Additional Services Requested.Select from above and reload
          </NoAccountsText>
        ) : (
          sectionsOfServices.additionalServicesAccount.map((account) => (
            <div key={account.id} id={account.id}>
              <AccountCollapsible
                caseId={caseId || ""}
                signature={signature || ""}
                account={account}
                section="blocked"
                open={account_id === account.id}
              />
            </div>
          ))
        )}
      </Section>
    </Stack>
  );
};

export type SectionProps = {
  readonly title: React.ReactNode;
  readonly children: React.ReactNode;
};

const Section: React.FC<SectionProps> = ({ title, children }) => {
  return (
    <Stack rowGap={1}>
      <Header level={2}>{title}</Header>
      <Stack rowGap={2}>{children}</Stack>
    </Stack>
  );
};

type NoAccountsTextProps = {
  readonly children: React.ReactNode;
};

const NoAccountsText: React.FC<NoAccountsTextProps> = ({ children }) => (
  <Typography variant="body1" color={colors.fadedText}>
    {children}
  </Typography>
);
